import React, { Suspense, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Panel } from 'react-bootstrap';
import _ from 'lodash';

import withAuthentication from '../../hoc/withAuthentication';
import withLayout from '../../hoc/withLayout';
 
import { useAlertStore } from '../../store/providers/Alert';
import { useNewsStore } from '../../store/providers/News';
import Permission from '../../components/Permission/Permission';

import { breadcrumbAddRoute, breadcrumbRemoveRoute } from '../../store/actions/breadcrumb';

import Spinner from '../../components/Spinner/Spinner';

import * as constants from '../../constants';
import { columns, sorting } from './NewsSchema';

const BaseList = React.lazy(() => import('../../components/Lists/BaseList/BaseList'));
const SideMenu = React.lazy(() => import('../../components/SideMenu/SideMenu'));
const PageHeader = React.lazy(() => import('../../components/PageHeader/PageHeader'));
const NewsModal = React.lazy(() => import('./Modals/NewsModal'));

function News() {
    const dispatch = useDispatch();

    const { addAlert } = useAlertStore();
    const { listRecords, deleteRecord, data, clearData } = useNewsStore();

    const [isMounted, setIsMounted] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [isBusy, setIsBusy] = useState(false);
    const [listData, setListData] = useState([]);
    const [modalTitle, setModalTitle] = useState('Add Company News');
    const [selectedNewsId, setSelectedNewsId] = useState();

    const pagePermission = constants.PERMISSION_CONTENT_COMPANYNEWS;
    const [hasPermission, setHasPermission] = useState(false);
    const [canEdit, setCanEdit] = useState(false);

    const selectedHandler = (news) => {
        setIsEditing(true);
        setModalTitle('Edit Company News');
        setSelectedNewsId(news.Id);
    };

    const addedHandler = () => {
        setIsEditing(true);
        setModalTitle('Add Company News');
        setSelectedNewsId();
    };

    const refreshedHandler = () => {
        listRecords();
    };

    const deletedHandler = (news) => {
        deleteRecord({ newsId: news.Id });
    };

    const cancelledHandler = () => {
        setIsEditing(false);
    };

    const submittedHandler = () => {
        setIsEditing(false);
        listRecords();
    };

    useEffect(() => {
        return () => {
            dispatch(breadcrumbRemoveRoute());
        };
    }, [dispatch]);

    useEffect(() => {
        if (!_.isEmpty(data)) {
            if (data.status === 'fail' || data.status === 'error') {
                addAlert({ key: constants.ALERT_SERVER_ERROR, text: <strong>{data.body}</strong>, type: constants.ALERT_TYPE_DANGER });
                clearData();
            }

            if (data.scope === 'delete' && data.status === 'success') {
                listRecords();
            }

            if (data.status === 'success' && data.scope === 'list') {
                setListData(data.body);
            }

            setIsBusy(data.status === 'processing' && (data.scope === 'delete' || data.scope === 'list'));
        }
    }, [data, addAlert, clearData, listRecords]);

    useEffect(() => {
        if (!hasPermission) { return; }
        if (isMounted) { return; }

        dispatch(breadcrumbAddRoute({ key: 'companyNews', parent: 'Content', text: 'Company News' }));
        listRecords();
        setIsMounted(true);
    }, [isMounted, hasPermission, listRecords, dispatch]);

    return (
        <>        
            <SideMenu activeKey="content" />
            <div className="content-body">

                <Permission showDenied={true} pagePermission={pagePermission} hasPermission={hasPermission} setHasPermission={setHasPermission} setCanEdit={setCanEdit}/>
                { hasPermission && isMounted &&

                    <Suspense fallback={<Spinner text="Loading..." />}>
                        {
                            isEditing && <NewsModal
                                show={isEditing}
                                canEdit={canEdit}
                                title={modalTitle}
                                newsId={selectedNewsId}
                                cancelled={() => cancelledHandler()}
                                submitted={() => submittedHandler()} />
                        }
                        <div style={{ paddingRight: '15px' }}>
                            <PageHeader title="Company News" />
                            <Panel>
                                <Panel.Body>
                                    <BaseList
                                        compType="Company News"
                                        listData={listData}
                                        isBusy={isBusy}
                                        sorting={sorting}
                                        columns={columns}
                                        hideCopy={true}
                                        hidePrint={true}
                                        hideFilter={true}
                                        permissionEdit={canEdit}
                                        added={() => addedHandler()}
                                        selected={(news) => selectedHandler(news)}
                                        deleted={(news) => deletedHandler(news)}
                                        refreshed={() => refreshedHandler()} />
                                </Panel.Body>
                            </Panel>
                        </div>
                    </Suspense>
                }
            </div>
        </>
    );
}

export default withAuthentication(withLayout(News));
